import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Card, Form, Table, Button, Badge, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import api from "../../../api";
import Select from "react-select";

const headerStyle = { 
 backgroundColor: "#92D050", 
 color: "#000", 
 fontWeight: "bold", 
 textAlign: "center", 
 verticalAlign: "middle", 
 border: "1px solid #000" 
};

const inputStyle = { 
 fontSize: "12px", 
 padding: "8px", 
 height: "auto", 
 minWidth: "80px",
 backgroundColor: "#fff"
};

const selectStyles = {
 menu: (provided) => ({ ...provided, zIndex: 9999 }),
 menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const EditableHeader = ({ label, keyName, rowSpan = 1, colSpan = 1, style = {}, onRemove = null, updateCustomHeader, isApproved = false }) => {
 return (
 <th 
 style={{ 
 ...headerStyle, 
 ...style, 
 position: "relative", 
 minWidth: colSpan > 1 ? "150px" : "100px",
 padding: "8px 12px",
 zIndex: 1
 }} 
 rowSpan={rowSpan} 
 colSpan={colSpan}
 >
 <div className="d-flex align-items-center justify-content-center w-100 h-100 position-relative group-header-container">
 <input
 type="text"
 className="form-control form-control-sm text-center fw-bold p-0 border-0 header-editable-input"
 style={{ 
 backgroundColor: "transparent", 
 color: "inherit", 
 fontSize: "12px",
 width: "calc(100% - 25px)",
 outline: "none",
 textTransform: "uppercase",
 minHeight: "30px",
 border: "1px solid transparent",
 borderRadius: "4px",
 cursor: isApproved ? "default" : "text"
 }}
 readOnly={isApproved}
 value={label}
 onChange={(e) => updateCustomHeader(keyName, e.target.value)}
 placeholder="Header"
 />
 {onRemove && !isApproved && (
 <button 
 type="button"
 className="btn btn-link text-danger p-0 border-0 remove-header-btn"
 style={{ 
 position: "absolute",
 right: "-6px",
 top: "50%",
 transform: "translateY(-50%)",
 fontSize: "18px",
 lineHeight: 1,
 textDecoration: "none",
 zIndex: 10,
 cursor: "pointer",
 background: "white",
 borderRadius: "50%",
 width: "20px",
 height: "20px",
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
 }}
 onClick={(e) => {
 e.stopPropagation();
 e.preventDefault();
 onRemove();
 }}
 >
 <i className="fa fa-times-circle"></i>
 </button>
 )}
 </div>
 </th>
 );
};

const MeasurementSheetEdit = () => {
 const fallbackSheetTypes = [
 { code: "ELECTRICAL_LIGHTING", name: "Electrical Lighting" },
 { code: "CONDUIT_MEASUREMENT", name: "Conduit Measurement" },
 { code: "CONDUIT_SUMMARY", name: "Conduit Summary" },
 { code: "SWITCH_SOCKET", name: "Switch & Socket" },
 { code: "DB_INSTALLATION", name: "DB Installation" },
 { code: "CABLE_MEASUREMENT", name: "Cable Measurement" },
 { code: "CABLE_TRAY_SUMMARY", name: "Cable Tray Summary" },
 { code: "WIRING_MEASUREMENT", name: "Wiring Measurement" },
 ];

 const { id } = useParams();
 const navigate = useNavigate();
 const DRAFT_KEY = `ms_edit_draft_${id}`;

 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [isAutoSaving, setIsAutoSaving] = useState(false);
 const isAutoSavingRef = useRef(false);
 const lastAutoSaveSignatureRef = useRef("");
 const [workOrders, setWorkOrders] = useState([]);
 const [sheetTypeMasters, setSheetTypeMasters] = useState([]);
 const [boqList, setBoqList] = useState([]);
 const [usedQtyMap, setUsedQtyMap] = useState({});

 const [formData, setFormData] = useState({
 project: "",
 workOrder: "",
 boqItem: "",
 sheetType: "ELECTRICAL_LIGHTING",
 location: "",
 area: "",
 package: "ELECTRICAL INSTALLATION",
 preparedDate: "",
 certifiedDate: "",
 status: "Draft",
 preparedByName: "",
 checkedByName: "",
 sheetNumber: "",
 itemName: "",
 description: "",
 unit: "",
 approvedQty: 0,
 usedQtyBefore: 0,
 remainingQtyBefore: 0,
 calculatedQty: 0,
 installedQty: 0,
 wlino: "",
 hsnSac: "",
 customHeaders: {},
 columnSettings: {},
 });

 const [columnConfig, setColumnConfig] = useState({});
 const [showModal, setShowModal] = useState(false);
 const [modalData, setModalData] = useState({ parent: "", subs: [""], fieldType: "number" });
 const [rows, setRows] = useState([]);

 const fetchUsedQuantities = useCallback(async (woId) => {
 try {
 const res = await api.get(`/measurement-sheets/used-qty/${woId}?excludeSheet=${id}`);
 if (res.data.success) setUsedQtyMap(res.data.map);
 } catch (err) { console.error("Failed to fetch used quantities", err); }
 }, [id]);

 const loadBOQ = useCallback(async (woId) => {
 try {
 const res = await api.get(`/work-orders/${woId}/boq`);
 if (res.data.success) setBoqList(res.data.data.boqItems || res.data.data);
 } catch (err) { console.error(err); }
 }, []);

 const checkAndRestoreDraft = useCallback(() => {
 const savedDraft = localStorage.getItem(DRAFT_KEY);
 if (savedDraft) {
 try {
 const { formData: dForm, rows: dRows, columnConfig: dCols } = JSON.parse(savedDraft);
 swal({
 title: "Restore Unsaved Changes?",
 text: "We found unsaved changes from your last session. Would you like to restore them?",
 icon: "info",
 buttons: ["No, Use Server Data", "Yes, Restore Changes"],
 }).then((willRestore) => {
 if (willRestore) {
 setFormData(dForm);
 setRows(dRows);
 setColumnConfig(dCols);
 loadBOQ(dForm.workOrder);
 fetchUsedQuantities(dForm.workOrder);
 } else {
 localStorage.removeItem(DRAFT_KEY);
 }
 });
 } catch (e) { console.error("Failed to parse draft", e); }
 }
 }, [DRAFT_KEY, loadBOQ, fetchUsedQuantities]);

 const defaultHeadersMap = {
 common: {
 srNo: "SR.NO", location: "LOCATION", uom: "UOM", from: "FROM", to: "TO", wireSize: "WIRE SIZES", circuitNo: "CIRCUIT NO.", conduitSize: "CONDUIT SIZE", remarks: "REMARKS", action: "ACTION",
 dbToSb: "DB TO SB", dbTo1stLight: "DB TO 1ST LIGHT", sbTo1stPoint: "SB TO 1ST POINT", sbToSb: "SB TO SB", pointToPoint: "POINT TO POINT", qty: "QTY", description: "DESCRIPTION / LOCATION", areaNo: "AREA NO.",
 pointFrom: "FROM", pointTo: "TO", pointQty: "QTY", powerFrom: "FROM", powerTo: "TO", powerQty: "QTY", subMainFrom: "FROM", subMainTo: "TO", subMainQty: "QTY"
 },
 ELECTRICAL_LIGHTING: { pointHeader: "LIGHT / FAN / EXHAUST POINT", powerHeader: "POWER POINTS", subMainHeader: "SUB MAINS" },
 CONDUIT_MEASUREMENT: { primaryHeader: "PRIMARY LIGHT/FAN/EXHAUST POINT", secondaryHeader: "SECONDARY POINTS", primaryPowerHeader: "PRIMARY POWER POINTS", secondaryPowerHeader: "SECONDARY POWER POINTS" },
 CONDUIT_SUMMARY: { conduitHeader: "LIGHT/FAN/EXHAUST, POWER POINT & SUB MAIN CONDUIT", elvConduitHeader: "ELV CONDUIT", col1: "20 MM", col2: "25 MM", col3: "32 MM", col4: "20 MM", col5: "25 MM", col6: "32 MM" },
 SWITCH_SOCKET: { col1: "6 AMP SWITCH", col2: "16 AMP SWITCH", col3: "6 AMP SOCKET", col4: "16 AMP SOCKET", col5: "1 MODULE", col6: "2 MODULE", col7: "3 MODULE", col8: "4 MODULE", col9: "6 MODULE", col10: "8 MODULE" },
 DB_INSTALLATION: { col1: "8 Way TPN DB", col2: "6 Way TPN DB", col3: "4 Way TPN DB", col4: "12 Way SPN DB", col5: "8 Way SPN DB", col6: "6 Way SPN DB", col7: "4 Way SPN DB", col8: "12 WAY VTPN" },
 CABLE_MEASUREMENT: { cableRunHeader: "CABLE RUN", cableQtyHeader: "CABLE QTY.", totalCablesHeader: "TOTAL CABLES" },
 CABLE_TRAY_SUMMARY: { lengthHeader: "LENGTH", runHeader: "RUN", perforated1Header: "PERFORATED", ladderHeader: "LADDER TRAY", stripHeader: "G.I STRIP", perforated2Header: "PERFORATED", tray300mm: "300 MM", tray450mm: "450 MM", tray600mm: "600 MM", ladder900mm: "900 MM", ladder1200mm: "1200 MM", strip25x3: "(25x3)MM GI", strip32x6: "(32x6)MM GI", tray150mm: "150 MM", tray100mm: "100 MM" },
 WIRING_MEASUREMENT: { primaryHeader: "PRIMARY LIGHT/FAN/EXHAUST POINT", secondaryHeader: "SECONDARY POINTS", primaryPowerHeader: "PRIMARY POWER POINTS", secondaryPowerHeader: "SECONDARY POWER POINTS", fanFixingHeader: "CEILING FAN FIXING", lightFixingHeader: "36 W. TUBE LIGHTS FIXING" }
 };

 const getHeaderLabel = (type, key, defaultLabel) => {
 const master = sheetTypeMasters.find((item) => item.code === type);
 const layout = getMasterLayout(master);
 const directLabel = formData.customHeaders?.[key] || layout.headers[key] || defaultHeadersMap[type]?.[key] || defaultHeadersMap.common?.[key];
 if (directLabel) return directLabel;
 const colMatch = String(key || "").match(/^col(\d+)(?:_group)?$/);
 if (master?.isSystem === false && colMatch) {
 const field = (master.fields || [])
 .filter((item) => item.defaultVisible !== false && !["srNo", "location", "areaNo", "uom", "remarks", "action"].includes(item.key))
 .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))[Number(colMatch[1]) - 1];
 if (field) return key.endsWith("_group") ? (field.groupLabel || field.label || field.key) : (field.label || field.key);
 }
 return defaultLabel;
 };

 const getMasterLayout = (master) => {
 const config = { ...(master?.defaultColumnConfig || {}) };
 const headers = { ...(master?.defaultHeaders || {}) };
 const settings = { ...(master?.defaultColumnSettings || {}) };

 if (master?.isSystem === false && Array.isArray(master?.fields) && master.fields.length > 0) {
 Object.keys(config).forEach((key) => delete config[key]);
 Object.keys(headers).forEach((key) => delete headers[key]);
 Object.keys(settings).forEach((key) => delete settings[key]);
 let customIndex = 1;
 master.fields
 .filter((field) => field.defaultVisible !== false)
 .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
 .forEach((field) => {
 const baseKey = ["srNo", "location", "areaNo", "uom", "remarks", "action"].includes(field.key) ? field.key : `col${customIndex++}`;
 config[baseKey] = true;
 headers[baseKey] = field.label || field.key || baseKey;
 settings[baseKey] = { ...(settings[baseKey] || {}), fieldType: field.inputType === "text" ? "text" : "number" };
 });
 }

 return { config, headers, settings };
 };

 const updateCustomHeader = (key, value) => {
 setFormData(prev => ({ ...prev, customHeaders: { ...prev.customHeaders, [key]: value } }));
 };

 const isColActive = useCallback((colKey) => {
 if (columnConfig[colKey] !== undefined) return columnConfig[colKey];
 const master = sheetTypeMasters.find((item) => item.code === formData.sheetType);
 const masterConfig = getMasterLayout(master).config;
 if (masterConfig && masterConfig[colKey] !== undefined) {
 return masterConfig[colKey];
 }
 const defaultActiveCols = [
 "srNo", "location", "uom", "from", "to", "wireSize", "circuitNo", "conduitSize", "remarks", "action",
 "pointHeader", "powerHeader", "subMainHeader", "primaryHeader", "secondaryHeader", "primaryPowerHeader", "secondaryPowerHeader",
 "fanFixingHeader", "lightFixingHeader", "conduitHeader", "elvConduitHeader", "cableRunHeader", "cableQtyHeader", "totalCablesHeader",
 "lengthHeader", "runHeader", "perforated1Header", "ladderHeader", "stripHeader", "perforated2Header",
 "tray300mm", "tray450mm", "tray600mm", "ladder900mm", "ladder1200mm", "strip25x3", "strip32x6", "tray150mm", "tray100mm",
 "description", "areaNo", "pointFrom", "pointTo", "pointQty", "powerFrom", "powerTo", "powerQty", "subMainFrom", "subMainTo", "subMainQty",
 "dbToSb", "dbTo1stLight", "sbTo1stPoint", "sbToSb", "pointToPoint"
 ];
 if (defaultActiveCols.includes(colKey)) return true;
 if (formData.sheetType === "SWITCH_SOCKET" && colKey.startsWith("col")) return parseInt(colKey.replace("col", "")) <= 10;
 if (formData.sheetType === "DB_INSTALLATION" && colKey.startsWith("col")) return parseInt(colKey.replace("col", "")) <= 8;
 if (formData.sheetType === "CONDUIT_SUMMARY" && ["col1", "col2", "col3", "col4", "col5", "col6"].includes(colKey)) return true;
 return false;
 }, [columnConfig, formData.sheetType, sheetTypeMasters]);

 const handleSheetTypeChange = (sheetType) => {
 const master = sheetTypeMasters.find((item) => item.code === sheetType);
 const layout = getMasterLayout(master);

 setFormData((prev) => ({
 ...prev,
 sheetType,
 customHeaders: layout.headers || prev.customHeaders || {},
 columnSettings: layout.settings || prev.columnSettings || {},
 }));

 setColumnConfig(layout.config || {});
 };

 const removeColumn = (colKey) => {
 setColumnConfig(prevConfig => {
 const newConfig = { ...prevConfig };
 setFormData(prevForm => {
 const newForm = { ...prevForm };
 const colSettings = newForm.columnSettings?.[colKey] || {};
 const colSpan = colSettings.colSpan || 1;
 if (colKey.startsWith("col")) {
 const num = parseInt(colKey.replace("col", ""));
 let parentKey = null;
 for (let i = 1; i < num; i++) {
 const pKey = `col${i}`;
 const pSpan = newForm.columnSettings?.[pKey]?.colSpan || 1;
 if (prevConfig[pKey] !== false && num < i + pSpan) { parentKey = pKey; break; }
 }
 if (parentKey) {
 newConfig[colKey] = false;
 const currentParentSpan = newForm.columnSettings[parentKey]?.colSpan || 1;
 newForm.columnSettings = { ...newForm.columnSettings, [parentKey]: { ...newForm.columnSettings[parentKey], colSpan: Math.max(1, currentParentSpan - 1) } };
 } else if (colSpan > 1) {
 for (let i = 0; i < colSpan; i++) { newConfig[`col${num + i}`] = false; }
 newConfig[colKey] = false;
 } else { newConfig[colKey] = false; }
 } else { newConfig[colKey] = false; }
 return newForm;
 });
 return newConfig;
 });
 };

 const fetchInitialData = useCallback(async () => {
 try {
 const [woRes, , sheetRes, typeRes] = await Promise.allSettled([
 api.get("/work-orders?limit=1000"),
 api.get("/boq-unit-types"),
 api.get(`/measurement-sheets/${id}`),
 api.get("/measurement-sheet-types"),
 ]);
 if (woRes.status === "fulfilled" && woRes.value.data.success) {
 setWorkOrders(woRes.value.data.data || []);
 }
 if (typeRes.status === "fulfilled" && typeRes.value.data.success) {
 setSheetTypeMasters(typeRes.value.data.data || fallbackSheetTypes);
 } else {
 setSheetTypeMasters(fallbackSheetTypes);
 }
 if (sheetRes.status === "fulfilled" && sheetRes.value.data.success) {
 const s = sheetRes.value.data.data;
 const initialForm = {
 ...s,
 project: s.project?._id || s.project || "",
 workOrder: s.workOrder?._id || s.workOrder || "",
 boqItem: Array.isArray(s.boqItem) ? s.boqItem : (s.boqItem ? [s.boqItem] : []),
 preparedDate: s.preparedDate ? new Date(s.preparedDate).toISOString().split("T")[0] : "",
 certifiedDate: s.certifiedDate ? new Date(s.certifiedDate).toISOString().split("T")[0] : "",
 };
 setFormData(initialForm);
 setColumnConfig(s.columnConfig || {});
 setRows(s.rows || []);
 lastAutoSaveSignatureRef.current = JSON.stringify({
 ...initialForm,
 rows: s.rows || [],
 columnConfig: s.columnConfig || {},
 });
 if (s.workOrder) {
 const woId = s.workOrder?._id || s.workOrder;
 loadBOQ(woId);
 fetchUsedQuantities(woId);
 }
 checkAndRestoreDraft();
 }
 } catch (err) { swal("Error", "Failed to load initial data", "error"); } finally { setLoading(false); }
 }, [id, loadBOQ, fetchUsedQuantities, checkAndRestoreDraft]);

 useEffect(() => { fetchInitialData(); }, [fetchInitialData]);

 useEffect(() => {
 if (!sheetTypeMasters.length) return;

 const master = sheetTypeMasters.find((item) => item.code === formData.sheetType);
 if (!master) return;
 const layout = getMasterLayout(master);
 const isCustomMaster = master.isSystem === false;
 const hasColumnConfig = Object.keys(columnConfig || {}).length > 0;

 if (hasColumnConfig && !isCustomMaster) return;

 if (isCustomMaster) {
 const needsConfigMerge = Object.keys(layout.config || {}).some((key) => columnConfig?.[key] === undefined);
 const needsHeaderMerge = Object.keys(layout.headers || {}).some((key) => formData.customHeaders?.[key] === undefined);
 const needsSettingsMerge = Object.keys(layout.settings || {}).some((key) => formData.columnSettings?.[key] === undefined);

 if (!needsConfigMerge && !needsHeaderMerge && !needsSettingsMerge) return;

 if (needsConfigMerge) {
 setColumnConfig((prev) => ({ ...(layout.config || {}), ...(prev || {}) }));
 }
 if (needsHeaderMerge || needsSettingsMerge) {
 setFormData((prev) => ({
 ...prev,
 customHeaders: { ...(layout.headers || {}), ...(prev.customHeaders || {}) },
 columnSettings: { ...(layout.settings || {}), ...(prev.columnSettings || {}) },
 }));
 }
 return;
 }

 setColumnConfig(layout.config || {});
 setFormData((prev) => ({
 ...prev,
 customHeaders: Object.keys(prev.customHeaders || {}).length ? prev.customHeaders : (layout.headers || {}),
 columnSettings: Object.keys(prev.columnSettings || {}).length ? prev.columnSettings : (layout.settings || {}),
 }));
 }, [sheetTypeMasters, formData.sheetType, columnConfig]);

 // ==========================
 // AUTO-SAVE DRAFT LOGIC
 // ==========================

 // 1. Sync to LocalStorage (Immediate safety net for sidebar clicks)
 useEffect(() => {
 if (loading) return;
 const hasData = formData.workOrder || formData.boqItem || rows.some(r => 
 (r.location && r.location.trim()) || 
 Object.entries(r).some(([key, val]) => typeof val === 'number' && val > 0 && !['srNo'].includes(key))
 );
 
 if (hasData && formData.status !== "Approved") {
 const draftData = { formData, rows, columnConfig };
 localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
 }
 }, [formData, rows, columnConfig, loading, DRAFT_KEY]);

 // 2. Sync to DB (Debounced for server efficiency)
 const autoSaveToDB = useCallback(async () => {
 // Only auto-save if essential fields are present and not already saving
 if (!formData.project || !formData.workOrder || !formData.boqItem || saving || isAutoSavingRef.current) {
 return;
 }

 // Don't auto-save if sheet is already approved
 if (formData.status === "Approved") return;

 try {
 const payload = {
 ...formData,
 rows,
 columnConfig,
 // Keep status as Draft or Submitted, but don't force change it
 };
 const saveSignature = JSON.stringify(payload);
 if (saveSignature === lastAutoSaveSignatureRef.current) return;

 isAutoSavingRef.current = true;
 setIsAutoSaving(true);
 await api.put(`/measurement-sheets/${id}`, payload, { suppressErrorToast: true });
 lastAutoSaveSignatureRef.current = saveSignature;
 console.log("Auto-saved draft (updated)");
 } catch (err) {
 console.error("Auto-save to DB failed", err);
 } finally {
 setIsAutoSaving(false);
 isAutoSavingRef.current = false;
 }
 }, [formData, rows, columnConfig, id, saving]);

 useEffect(() => {
 if (loading) return; // Don't auto-save while initial loading

 const timer = setTimeout(() => {
 autoSaveToDB();
 }, 2000); // Debounce save for 2 seconds

 return () => clearTimeout(timer);
 }, [formData, rows, columnConfig, autoSaveToDB, loading]);

 // Handle accidental closure/refresh
 useEffect(() => {
 const handleBeforeUnload = (e) => {
 if (formData.status !== "Approved") {
 const msg = "You have unsaved changes. Are you sure you want to leave?";
 e.returnValue = msg;
 return msg;
 }
 };
 window.addEventListener("beforeunload", handleBeforeUnload);
 return () => window.removeEventListener("beforeunload", handleBeforeUnload);
 }, [formData.status]);

 // Handle internal navigation
 useEffect(() => {
 return () => {
 if (formData.status !== "Approved") {
 autoSaveToDB();
 }
 };
 }, [autoSaveToDB, formData.status]);

 const handleWorkOrderChange = (opt) => {
 const wo = workOrders.find(w => w._id === opt.value);
 setFormData(prev => ({ ...prev, workOrder: opt.value, project: wo.project?._id || wo.project || "", location: wo.project?.location || wo.location || "", area: wo.project?.area || wo.area || "" }));
 loadBOQ(opt.value);
 fetchUsedQuantities(opt.value);
 };

 const handleBOQChange = (opts) => {
 const isMulti = Array.isArray(opts);
 const selectedValues = isMulti ? opts.map(o => o.value) : (opts ? [opts.value] : []);
 
 // For single select backward compatibility or reference logic
 const firstBoqId = selectedValues[0];
 const firstBoq = boqList.find((b) => b._id === firstBoqId);
 
 let totalApproved = 0;
 let totalUsed = 0;
 
 selectedValues.forEach(id => {
 const b = boqList.find(x => x._id === id);
 if (b) {
 totalApproved += Number(b.quantity || 0);
 totalUsed += Number(usedQtyMap[id] || 0);
 }
 });

 setFormData((prev) => ({ 
 ...prev, 
 boqItem: selectedValues, 
 itemName: firstBoq?.itemName || (selectedValues.length > 1 ? `${selectedValues.length} items selected` : ""), 
 description: firstBoq?.description || "", 
 unit: firstBoq?.unit || "", 
 approvedQty: totalApproved, 
 usedQtyBefore: totalUsed, 
 remainingQtyBefore: totalApproved - totalUsed, 
 wlino: firstBoq?.wlino || "", 
 hsnSac: firstBoq?.hsnSac || "" 
 }));
 if (firstBoqId) {
 setRows(prevRows => clampAllRowsToBoqCaps(prevRows.map((r) => ({
 ...r,
 uom: firstBoq?.unit || r.uom,
 boqItemId: selectedValues.includes(r.boqItemId) ? r.boqItemId : firstBoqId,
 })), selectedValues));
 }
 };

 const numericRowFields = new Set([
 "pointQty", "powerQty", "subMainQty",
 "primaryPointDbToSb", "primaryPointDbTo1stLight", "primaryPointSbTo1stPoint",
 "secondaryPointSbToSb", "secondaryPointPointToPoint",
 "dbToSb", "dbTo1stLight", "sbTo1stPoint", "sbToSb", "pointToPoint",
 "primaryPowerPoints", "secondaryPowerPoints",
 "conduit20mm", "conduit25mm", "conduit32mm",
 "elvConduit20mm", "elvConduit25mm", "elvConduit32mm",
 "cableRun", "cableQty", "totalCables",
 "length", "run",
 "tray300mm", "tray450mm", "tray600mm",
 "ladder900mm", "ladder1200mm",
 "strip25x3", "strip32x6",
 "tray150mm", "tray100mm",
 "fanFixing", "lightFixing",
 ]);
 for (let i = 1; i <= 20; i++) numericRowFields.add(`col${i}`);

 const toNonNegativeNumber = (value) => {
 const numberValue = Number(value);
 if (!Number.isFinite(numberValue)) return 0;
 return Math.max(0, numberValue);
 };

 const getRemainingQty = (data = formData) => Math.max(0, Number(data.approvedQty || 0) - Number(data.usedQtyBefore || 0));

 const getBoqRemainingQty = (boqId) => {
 const boq = boqList.find((item) => item._id === boqId);
 if (!boq) return getRemainingQty();
 return Math.max(0, Number(boq.quantity || 0) - Number(usedQtyMap[boqId] || 0));
 };

 const getRowBoqId = (row) => row?.boqItemId || formData.boqItem?.[0] || "";

 const isNumericRowField = (field) => {
 if (/^col\d+$/.test(field)) {
 return (formData.columnSettings?.[field]?.fieldType || "number") === "number";
 }
 return numericRowFields.has(field);
 };

 const getCalculatedQtyFromTotals = (nextTotals, sheetType = formData.sheetType) => {
 if (sheetType === "ELECTRICAL_LIGHTING") return nextTotals.pointQty + nextTotals.powerQty + nextTotals.subMainQty;
 if (sheetType === "CONDUIT_MEASUREMENT") return nextTotals.primaryPointDbToSb + nextTotals.primaryPointDbTo1stLight + nextTotals.primaryPointSbTo1stPoint + nextTotals.secondaryPointSbToSb + nextTotals.secondaryPointPointToPoint + nextTotals.primaryPowerPoints + nextTotals.secondaryPowerPoints;
 if (sheetType === "CONDUIT_SUMMARY") return nextTotals.conduit20mm + nextTotals.conduit25mm + nextTotals.conduit32mm + nextTotals.elvConduit20mm + nextTotals.elvConduit25mm + nextTotals.elvConduit32mm;
 if (sheetType === "SWITCH_SOCKET" || sheetType === "DB_INSTALLATION") {
 let total = 0;
 for (let i = 1; i <= 20; i++) {
 if (isColActive(`col${i}`)) total += nextTotals[`col${i}`];
 }
 return total;
 }
 if (sheetType === "CABLE_MEASUREMENT") return nextTotals.totalCables;
 if (sheetType === "CABLE_TRAY_SUMMARY") return nextTotals.tray300mm + nextTotals.tray450mm + nextTotals.tray600mm + nextTotals.ladder900mm + nextTotals.ladder1200mm + nextTotals.strip25x3 + nextTotals.strip32x6 + nextTotals.tray150mm + nextTotals.tray100mm;
 if (sheetType === "WIRING_MEASUREMENT") return nextTotals.primaryPointDbToSb + nextTotals.primaryPointDbTo1stLight + nextTotals.primaryPointSbTo1stPoint + nextTotals.secondaryPointSbToSb + nextTotals.secondaryPointPointToPoint + nextTotals.primaryPowerPoints + nextTotals.secondaryPowerPoints + nextTotals.fanFixing + nextTotals.lightFixing;
 let customTotal = 0;
 for (let i = 1; i <= 20; i++) {
 const colKey = `col${i}`;
 if (isColActive(colKey) && (formData.columnSettings?.[colKey]?.fieldType || "number") === "number") {
 customTotal += Number(nextTotals[colKey] || 0);
 }
 }
 return customTotal;
 };

 const getRowCalculatedQty = (row) => getCalculatedQtyFromTotals(calculateTotals([row]));

 const hasQuantityFields = () => {
 const quantitySheetTypes = [
 "ELECTRICAL_LIGHTING", "CONDUIT_MEASUREMENT", "CONDUIT_SUMMARY",
 "SWITCH_SOCKET", "DB_INSTALLATION", "CABLE_MEASUREMENT",
 "CABLE_TRAY_SUMMARY", "WIRING_MEASUREMENT",
 ];
 if (quantitySheetTypes.includes(formData.sheetType)) return true;
 for (let i = 1; i <= 20; i++) {
 const colKey = `col${i}`;
 if (isColActive(colKey) && (formData.columnSettings?.[colKey]?.fieldType || "number") === "number") return true;
 }
 return false;
 };

 const rowHasData = (row) => Object.entries(row).some(([key, value]) => {
 if (["srNo", "boqItemId", "uom"].includes(key)) return false;
 if (typeof value === "number") return value > 0;
 if (typeof value === "string") return value.trim() !== "";
 return Boolean(value);
 });

 const getRowsQtyForBoq = (nextRows, boqId) => {
 return nextRows.reduce((total, row) => {
 if (getRowBoqId(row) !== boqId) return total;
 return total + getRowCalculatedQty(row);
 }, 0);
 };

 const clampRowsToRemainingQty = (nextRows, index, field, desiredValue) => {
 const boqId = getRowBoqId(nextRows[index]);
 const remainingQty = boqId ? getBoqRemainingQty(boqId) : getRemainingQty();
 if (!isNumericRowField(field) || remainingQty <= 0) {
 nextRows[index][field] = 0;
 return nextRows;
 }
 const nextCalculatedQty = boqId ? getRowsQtyForBoq(nextRows, boqId) : getCalculatedQtyFromTotals(calculateTotals(nextRows));
 if (nextCalculatedQty <= remainingQty) return nextRows;

 let low = 0;
 let high = desiredValue;
 let best = 0;
 for (let i = 0; i < 24; i++) {
 const mid = (low + high) / 2;
 const testRows = nextRows.map((row, rowIndex) => {
 if (rowIndex !== index) return row;
 const updatedRow = { ...row, [field]: mid };
 if (formData.sheetType === "CABLE_MEASUREMENT" && (field === "cableRun" || field === "cableQty")) {
 updatedRow.totalCables = Number((Number(updatedRow.cableRun || 0) * Number(updatedRow.cableQty || 0)).toFixed(2));
 }
 return updatedRow;
 });
 const testQty = boqId ? getRowsQtyForBoq(testRows, boqId) : getCalculatedQtyFromTotals(calculateTotals(testRows));
 if (testQty <= remainingQty) {
 best = mid;
 low = mid;
 } else {
 high = mid;
 }
 }
 nextRows[index][field] = Number(best.toFixed(2));
 if (formData.sheetType === "CABLE_MEASUREMENT" && (field === "cableRun" || field === "cableQty")) {
 nextRows[index].totalCables = Number((Number(nextRows[index].cableRun || 0) * Number(nextRows[index].cableQty || 0)).toFixed(2));
 }
 return nextRows;
 };

 const clearNumericRowValues = (row) => {
 const nextRow = { ...row };
 numericRowFields.forEach((field) => {
 if (isNumericRowField(field)) nextRow[field] = 0;
 });
 return nextRow;
 };

 function clampAllRowsToBoqCaps(sourceRows, selectedBoqIds = formData.boqItem || []) {
 const usedByBoq = {};
 return sourceRows.map((row) => {
 const fallbackBoqId = selectedBoqIds[0] || "";
 const boqId = selectedBoqIds.includes(row.boqItemId) ? row.boqItemId : fallbackBoqId;
 let nextRow = { ...row, boqItemId: boqId };
 const remainingQty = boqId ? getBoqRemainingQty(boqId) : getRemainingQty();
 if (remainingQty <= 0) return clearNumericRowValues(nextRow);

 const allowedQty = Math.max(0, remainingQty - Number(usedByBoq[boqId] || 0));
 const rowQty = getRowCalculatedQty(nextRow);
 if (rowQty > allowedQty) {
 nextRow = clearNumericRowValues(nextRow);
 } else {
 usedByBoq[boqId] = Number(usedByBoq[boqId] || 0) + rowQty;
 }
 return nextRow;
 });
 }

 const handleRowChange = (index, field, value) => {
 setRows(prevRows => {
 const newRows = [...prevRows];
 if (field === "boqItemId") {
 const currentRow = { ...newRows[index], boqItemId: value };
 newRows[index] = getBoqRemainingQty(value) <= 0 ? clearNumericRowValues(currentRow) : currentRow;
 return newRows;
 }
 const nextValue = isNumericRowField(field) ? toNonNegativeNumber(value) : value;
 newRows[index] = { ...newRows[index], [field]: nextValue };
 if (formData.sheetType === "CABLE_MEASUREMENT" && (field === "cableRun" || field === "cableQty")) {
 newRows[index].totalCables = Number((Number(newRows[index].cableRun || 0) * Number(newRows[index].cableQty || 0)).toFixed(2));
 }
 if (isNumericRowField(field)) return clampRowsToRemainingQty(newRows, index, field, nextValue);
 return newRows;
 });
 };

 const addRow = () => { setRows(prevRows => [...prevRows, { srNo: prevRows.length + 1, boqItemId: formData.boqItem?.[0] || "", location: "", uom: formData.unit || "NOS", wireSize: "", circuitNumber: "", conduitSize: "", pointFrom: "", pointTo: "", pointQty: 0, powerFrom: "", powerTo: "", powerQty: 0, subMainFrom: "", subMainTo: "", subMainQty: 0, primaryPointDbToSb: 0, primaryPointDbTo1stLight: 0, primaryPointSbTo1stPoint: 0, secondaryPointSbToSb: 0, secondaryPointPointToPoint: 0, primaryPowerPoints: 0, secondaryPowerPoints: 0, col1: 0, col2: 0, col3: 0, col4: 0, col5: 0, col6: 0, col7: 0, col8: 0, col9: 0, col10: 0, col11: 0, col12: 0, col13: 0, col14: 0, col15: 0, col16: 0, col17: 0, col18: 0, col19: 0, col20: 0, conduit20mm: 0, conduit25mm: 0, conduit32mm: 0, elvConduit20mm: 0, elvConduit25mm: 0, elvConduit32mm: 0, length: 0, run: 0, tray300mm: 0, tray450mm: 0, tray600mm: 0, ladder900mm: 0, ladder1200mm: 0, strip25x3: 0, strip32x6: 0, tray150mm: 0, tray100mm: 0, fanFixing: 0, lightFixing: 0, cableFrom: "", cableTo: "", remarks: "" }]); };
 const removeRow = (idx) => { setRows(prevRows => { if (prevRows.length > 1) { return prevRows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, srNo: i + 1 })); } return prevRows; }); };

 const calculateTotals = (sourceRows = rows) => {
 return sourceRows.reduce((acc, row) => {
 acc.pointQty += Number(row.pointQty || 0); acc.powerQty += Number(row.powerQty || 0); acc.subMainQty += Number(row.subMainQty || 0);
 acc.primaryPointDbToSb += Number(row.primaryPointDbToSb || 0); acc.primaryPointDbTo1stLight += Number(row.primaryPointDbTo1stLight || 0); acc.primaryPointSbTo1stPoint += Number(row.primaryPointSbTo1stPoint || 0);
 acc.secondaryPointSbToSb += Number(row.secondaryPointSbToSb || 0); acc.secondaryPointPointToPoint += Number(row.secondaryPointPointToPoint || 0);
 acc.primaryPowerPoints += Number(row.primaryPowerPoints || 0); acc.secondaryPowerPoints += Number(row.secondaryPowerPoints || 0);
 acc.conduit20mm += Number(row.col1 || row.conduit20mm || 0); acc.conduit25mm += Number(row.col2 || row.conduit25mm || 0); acc.conduit32mm += Number(row.col3 || row.conduit32mm || 0);
 acc.elvConduit20mm += Number(row.col4 || row.elvConduit20mm || 0); acc.elvConduit25mm += Number(row.col5 || row.elvConduit25mm || 0); acc.elvConduit32mm += Number(row.col6 || row.elvConduit32mm || 0);
 acc.cableRun += Number(row.cableRun || 0); acc.cableQty += Number(row.cableQty || 0); acc.totalCables += Number(row.totalCables || 0);
 acc.tray300mm += Number(row.tray300mm || 0); acc.tray450mm += Number(row.tray450mm || 0); acc.tray600mm += Number(row.tray600mm || 0);
 acc.ladder900mm += Number(row.ladder900mm || 0); acc.ladder1200mm += Number(row.ladder1200mm || 0); acc.strip25x3 += Number(row.strip25x3 || 0); acc.strip32x6 += Number(row.strip32x6 || 0);
 acc.tray150mm += Number(row.tray150mm || 0); acc.tray100mm += Number(row.tray100mm || 0); acc.fanFixing += Number(row.fanFixing || 0); acc.lightFixing += Number(row.lightFixing || 0);
 for (let i = 1; i <= 20; i++) {
 const val = Number(row[`col${i}`] || 0);
 if (!Number.isNaN(val)) acc[`col${i}`] += val;
 }
 return acc;
 }, { pointQty: 0, powerQty: 0, subMainQty: 0, primaryPointDbToSb: 0, primaryPointDbTo1stLight: 0, primaryPointSbTo1stPoint: 0, secondaryPointSbToSb: 0, secondaryPointPointToPoint: 0, primaryPowerPoints: 0, secondaryPowerPoints: 0, conduit20mm: 0, conduit25mm: 0, conduit32mm: 0, elvConduit20mm: 0, elvConduit25mm: 0, elvConduit32mm: 0, cableRun: 0, cableQty: 0, totalCables: 0, tray300mm: 0, tray450mm: 0, tray600mm: 0, ladder900mm: 0, ladder1200mm: 0, strip25x3: 0, strip32x6: 0, tray150mm: 0, tray100mm: 0, fanFixing: 0, lightFixing: 0, col1: 0, col2: 0, col3: 0, col4: 0, col5: 0, col6: 0, col7: 0, col8: 0, col9: 0, col10: 0, col11: 0, col12: 0, col13: 0, col14: 0, col15: 0, col16: 0, col17: 0, col18: 0, col19: 0, col20: 0 });
 };

 const openGroupModal = () => { setModalData({ parent: "", subs: [""], fieldType: "number" }); setShowModal(true); };
 const handleModalSave = () => {
 if (!modalData.parent.trim()) return swal("Error", "Heading name is required", "error");
 const validSubs = modalData.subs.filter(s => s.trim());
 const subCount = validSubs.length || 1;
 
 // Find available columns that are NOT currently active
 let startNum = -1;
 for (let i = 1; i <= 20; i++) {
 let available = true;
 for (let j = 0; j < subCount; j++) {
 const colKey = `col${i + j}`;
 if (columnConfig[colKey] === true || (i + j) > 20) { 
 available = false; 
 break; 
 }
 }
 if (available) { startNum = i; break; }
 }

 if (startNum === -1) return swal("Error", "Not enough available columns to add this group", "error");

 const parentKey = `col${startNum}`;
 const newCustomHeaders = { ...formData.customHeaders };
 const newColumnSettings = { ...(formData.columnSettings || {}) };
 const updates = {};

 if (validSubs.length > 0) {
 // CASE 1: Heading with Sub-headings
 newCustomHeaders[`${parentKey}_group`] = modalData.parent;
 newColumnSettings[parentKey] = { 
 ...newColumnSettings[parentKey], 
 colSpan: subCount,
 fieldType: modalData.fieldType,
 isGroupParent: true 
 };

 validSubs.forEach((subName, idx) => { 
 const colKey = `col${startNum + idx}`; 
 updates[colKey] = true; 
 newCustomHeaders[colKey] = subName; 
 newColumnSettings[colKey] = { ...newColumnSettings[colKey], fieldType: modalData.fieldType };
 });
 } else {
 // CASE 2: Heading with NO Sub-headings (Single Column)
 const colKey = parentKey;
 updates[colKey] = true;
 newCustomHeaders[colKey] = modalData.parent;
 newColumnSettings[colKey] = { 
 ...newColumnSettings[colKey], 
 colSpan: 1, 
 fieldType: modalData.fieldType,
 isGroupParent: false
 };
 }

 setFormData(prev => ({ 
 ...prev, 
 customHeaders: newCustomHeaders, 
 columnSettings: newColumnSettings 
 }));
 
 setColumnConfig(prev => ({ ...prev, ...updates }));
 setShowModal(false);
 setModalData({ parent: "", subs: [""], fieldType: "number" });
 };

 const totals = calculateTotals();

 useEffect(() => {
 if (formData.status === "Approved" || loading) return;
 const { sheetType } = formData;
 const q = getCalculatedQtyFromTotals(totals, sheetType);
 setFormData(prev => {
 const newCalculated = Math.max(0, Number(q.toFixed(2)));
 const remainingQty = getRemainingQty(prev);
 const shouldUpdateInstalled = prev.installedQty === 0 || prev.installedQty === prev.calculatedQty;
 const nextInstalledQty = shouldUpdateInstalled ? newCalculated : toNonNegativeNumber(prev.installedQty);
 const nextInstalled = Math.min(nextInstalledQty, remainingQty);
 if (Number(prev.calculatedQty || 0) === newCalculated && Number(prev.installedQty || 0) === nextInstalled) return prev;
 return { ...prev, calculatedQty: newCalculated, installedQty: nextInstalled };
 });
 }, [totals, formData.sheetType, loading, formData.status, isColActive]);

 const handleSubmit = async (e) => {
 e.preventDefault();
 const calculatedQty = toNonNegativeNumber(getCalculatedQtyFromTotals(calculateTotals(), formData.sheetType));
 const installedQty = toNonNegativeNumber(formData.installedQty || formData.calculatedQty || calculatedQty);
 const remainingQty = getRemainingQty();
 // if (hasQuantityFields() && installedQty <= 0) return swal("Error", "Installed quantity must be greater than 0", "error");
 if (installedQty > remainingQty) {
 return swal("Error", `Installed quantity (${installedQty}) exceeds remaining BOQ quantity (${remainingQty.toFixed(2)})`, "error");
 }
 const validRows = rows.filter(rowHasData);
 if (validRows.length === 0) return swal("Error", "Please add at least one row with data", "error");
 setSaving(true);
 try {
 const data = { ...formData, installedQty, rows: validRows, totals: calculateTotals(), columnConfig };
 const res = await api.put(`/measurement-sheets/${id}`, data);
 if (res.data.success) { localStorage.removeItem(DRAFT_KEY); swal("Success", "Updated successfully", "success"); navigate(-1); }
 } catch (err) { swal("Error", err.response?.data?.message || err.message, "error"); } finally { setSaving(false); }
 };

 if (loading) return <div className="text-center p-5"><i className="fa fa-spinner fa-spin fa-3x"></i><p className="mt-2">Loading...</p></div>;

 const renderRow1 = () => {
 const { sheetType } = formData;
 const cells = [];
 if (formData.boqItem && formData.boqItem.length > 1) {
 cells.push(<EditableHeader key="boqRow" label="BOQ ITEM" keyName="boqRow" rowSpan={2} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("srNo")) cells.push(<EditableHeader key="sr" label={getHeaderLabel(sheetType, "srNo", "SR.NO")} keyName="srNo" rowSpan={2} onRemove={() => removeColumn("srNo")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 const locLabel = sheetType === "SWITCH_SOCKET" || sheetType === "DB_INSTALLATION" ? "DESCRIPTION / LOCATION" : "LOCATION";
 if (isColActive("location")) cells.push(<EditableHeader key="loc" label={getHeaderLabel(sheetType, "location", locLabel)} keyName="location" rowSpan={2} style={{ minWidth: "200px" }} onRemove={() => removeColumn("location")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("areaNo")) cells.push(<EditableHeader key="areaN" label={getHeaderLabel(sheetType, "areaNo", "AREA NO.")} keyName="areaNo" rowSpan={2} onRemove={() => removeColumn("areaNo")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("uom")) cells.push(<EditableHeader key="uom" label={getHeaderLabel(sheetType, "uom", "UOM")} keyName="uom" rowSpan={2} onRemove={() => removeColumn("uom")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (sheetType === "CONDUIT_SUMMARY" || sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("from")) cells.push(<EditableHeader key="from" label={getHeaderLabel(sheetType, "from", "FROM")} keyName="from" rowSpan={2} onRemove={() => removeColumn("from")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("to")) cells.push(<EditableHeader key="to" label={getHeaderLabel(sheetType, "to", "TO")} keyName="to" rowSpan={2} onRemove={() => removeColumn("to")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (["ELECTRICAL_LIGHTING", "CONDUIT_MEASUREMENT", "CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType)) {
 if (!["CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType) && isColActive("wireSize")) cells.push(<EditableHeader key="wire" label={getHeaderLabel(sheetType, "wireSize", "WIRE SIZES")} keyName="wireSize" rowSpan={2} style={{ minWidth: "120px" }} onRemove={() => removeColumn("wireSize")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("circuitNo")) cells.push(<EditableHeader key="circ" label={getHeaderLabel(sheetType, "circuitNo", "CIRCUIT NO.")} keyName="circuitNo" rowSpan={2} style={{ minWidth: "120px" }} onRemove={() => removeColumn("circuitNo")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("conduitSize")) cells.push(<EditableHeader key="conduit" label={getHeaderLabel(sheetType, "conduitSize", "CONDUIT SIZE")} keyName="conduitSize" rowSpan={2} style={{ minWidth: "120px" }} onRemove={() => removeColumn("conduitSize")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("pointHeader")) cells.push(<EditableHeader key="pHead" label={getHeaderLabel(sheetType, "pointHeader", "LIGHT / FAN / EXHAUST POINT")} keyName="pointHeader" colSpan={3} onRemove={() => removeColumn("pointHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("powerHeader")) cells.push(<EditableHeader key="powHead" label={getHeaderLabel(sheetType, "powerHeader", "POWER POINTS")} keyName="powerHeader" colSpan={3} onRemove={() => removeColumn("powerHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("subMainHeader")) cells.push(<EditableHeader key="subHead" label={getHeaderLabel(sheetType, "subMainHeader", "SUB MAINS")} keyName="subMainHeader" colSpan={3} onRemove={() => removeColumn("subMainHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) cells.push(<EditableHeader key="pri" label={getHeaderLabel(sheetType, "primaryHeader", "PRIMARY POINT")} keyName="primaryHeader" colSpan={3} onRemove={() => removeColumn("primaryHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("secondaryHeader")) cells.push(<EditableHeader key="sec" label={getHeaderLabel(sheetType, "secondaryHeader", "SECONDARY POINTS")} keyName="secondaryHeader" colSpan={2} onRemove={() => removeColumn("secondaryHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("primaryPowerHeader")) cells.push(<EditableHeader key="priP" label={getHeaderLabel(sheetType, "primaryPowerHeader", "PRI POWER")} keyName="primaryPowerHeader" rowSpan={2} onRemove={() => removeColumn("primaryPowerHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("secondaryPowerHeader")) cells.push(<EditableHeader key="secP" label={getHeaderLabel(sheetType, "secondaryPowerHeader", "SEC POWER")} keyName="secondaryPowerHeader" rowSpan={2} onRemove={() => removeColumn("secondaryPowerHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("fanFixingHeader")) cells.push(<EditableHeader key="fan" label={getHeaderLabel(sheetType, "fanFixingHeader", "FAN FIXING")} keyName="fanFixingHeader" rowSpan={2} onRemove={() => removeColumn("fanFixingHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("lightFixingHeader")) cells.push(<EditableHeader key="light" label={getHeaderLabel(sheetType, "lightFixingHeader", "LIGHT FIXING")} keyName="lightFixingHeader" rowSpan={2} onRemove={() => removeColumn("lightFixingHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 } else if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) cells.push(<EditableHeader key="conH" label={getHeaderLabel(sheetType, "conduitHeader", "CONDUIT")} keyName="conduitHeader" colSpan={3} onRemove={() => removeColumn("conduitHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("elvConduitHeader")) cells.push(<EditableHeader key="elvH" label={getHeaderLabel(sheetType, "elvConduitHeader", "ELV CONDUIT")} keyName="elvConduitHeader" colSpan={3} onRemove={() => removeColumn("elvConduitHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 } else if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("cableRunHeader")) cells.push(<EditableHeader key="cRun" label={getHeaderLabel(sheetType, "cableRunHeader", "CABLE RUN")} keyName="cableRunHeader" rowSpan={2} onRemove={() => removeColumn("cableRunHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("cableQtyHeader")) cells.push(<EditableHeader key="cQty" label={getHeaderLabel(sheetType, "cableQtyHeader", "CABLE QTY.")} keyName="cableQtyHeader" rowSpan={2} onRemove={() => removeColumn("cableQtyHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("totalCablesHeader")) cells.push(<EditableHeader key="cTotal" label={getHeaderLabel(sheetType, "totalCablesHeader", "TOTAL CABLES")} keyName="totalCablesHeader" rowSpan={2} onRemove={() => removeColumn("totalCablesHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("lengthHeader")) cells.push(<EditableHeader key="lenH" label={getHeaderLabel(sheetType, "lengthHeader", "LENGTH")} keyName="lengthHeader" rowSpan={2} onRemove={() => removeColumn("lengthHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("runHeader")) cells.push(<EditableHeader key="runH" label={getHeaderLabel(sheetType, "runHeader", "RUN")} keyName="runHeader" rowSpan={2} onRemove={() => removeColumn("runHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("perforated1Header")) cells.push(<EditableHeader key="perf1" label={getHeaderLabel(sheetType, "perforated1Header", "PERFORATED")} keyName="perforated1Header" colSpan={3} onRemove={() => removeColumn("perforated1Header")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("ladderHeader")) cells.push(<EditableHeader key="ladH" label={getHeaderLabel(sheetType, "ladderHeader", "LADDER TRAY")} keyName="ladderHeader" colSpan={2} onRemove={() => removeColumn("ladderHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("stripHeader")) cells.push(<EditableHeader key="strH" label={getHeaderLabel(sheetType, "stripHeader", "G.I STRIP")} keyName="stripHeader" colSpan={2} onRemove={() => removeColumn("stripHeader")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("perforated2Header")) cells.push(<EditableHeader key="perf2" label={getHeaderLabel(sheetType, "perforated2Header", "PERFORATED")} keyName="perforated2Header" colSpan={2} onRemove={() => removeColumn("perforated2Header")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 for (let num = 1; num <= 20; num++) {
 const colKey = `col${num}`;
 if (!isColActive(colKey)) continue;

 let parentKey = null;
 for (let i = 1; i < num; i++) {
 const pKey = `col${i}`;
 const pSpan = formData.columnSettings?.[pKey]?.colSpan || 1;
 if (isColActive(pKey) && num < i + pSpan) {
 parentKey = pKey;
 break;
 }
 }

 if (parentKey) continue;

 const colSettings = formData.columnSettings?.[colKey] || {};
 const colSpan = colSettings.colSpan || 1;
 const isGroupParent = colSettings.isGroupParent || colSpan > 1;
 const labelKey = isGroupParent ? `${colKey}_group` : colKey;

 cells.push(
 <EditableHeader
 key={colKey}
 label={getHeaderLabel(sheetType, labelKey, `Col ${num}`)}
 keyName={labelKey}
 rowSpan={isGroupParent ? 1 : 2}
 colSpan={colSpan}
 onRemove={() => removeColumn(colKey)}
 updateCustomHeader={updateCustomHeader}
 isApproved={formData.status === "Approved"}
 />
 );
 }
 if (isColActive("remarks")) cells.push(<EditableHeader key="rem" label={getHeaderLabel(sheetType, "remarks", "REMARKS")} keyName="remarks" rowSpan={2} style={{ minWidth: "150px" }} onRemove={() => removeColumn("remarks")} updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 if (isColActive("action") && formData.status !== "Approved") cells.push(<th key="act" style={{ ...headerStyle, borderRight: "1px solid #000" }} rowSpan={2}>ACTION</th>);
 return cells;
 };

 const renderRow2 = () => {
 const { sheetType } = formData;
 const cells = [];
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("pointHeader")) {
 cells.push(<EditableHeader key="pf" label={getHeaderLabel(sheetType, "pointFrom", "FROM")} keyName="pointFrom" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="pt" label={getHeaderLabel(sheetType, "pointTo", "TO")} keyName="pointTo" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="pq" label={getHeaderLabel(sheetType, "pointQty", "QTY")} keyName="pointQty" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("powerHeader")) {
 cells.push(<EditableHeader key="pwf" label={getHeaderLabel(sheetType, "powerFrom", "FROM")} keyName="powerFrom" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="pwt" label={getHeaderLabel(sheetType, "powerTo", "TO")} keyName="powerTo" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="pwq" label={getHeaderLabel(sheetType, "powerQty", "QTY")} keyName="powerQty" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("subMainHeader")) {
 cells.push(<EditableHeader key="smf" label={getHeaderLabel(sheetType, "subMainFrom", "FROM")} keyName="subMainFrom" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="smt" label={getHeaderLabel(sheetType, "subMainTo", "TO")} keyName="subMainTo" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="smq" label={getHeaderLabel(sheetType, "subMainQty", "QTY")} keyName="subMainQty" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) {
 cells.push(<EditableHeader key="dbsb" label={getHeaderLabel(sheetType, "dbToSb", "DB TO SB")} keyName="dbToSb" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="db1l" label={getHeaderLabel(sheetType, "dbTo1stLight", "DB TO 1ST LIGHT")} keyName="dbTo1stLight" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="sb1p" label={getHeaderLabel(sheetType, "sbTo1stPoint", "SB TO 1ST POINT")} keyName="sbTo1stPoint" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("secondaryHeader")) {
 cells.push(<EditableHeader key="sbsb" label={getHeaderLabel(sheetType, "sbToSb", "SB TO SB")} keyName="sbToSb" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="ptp" label={getHeaderLabel(sheetType, "pointToPoint", "POINT TO POINT")} keyName="pointToPoint" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 } else if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) {
 cells.push(<EditableHeader key="c1" label={getHeaderLabel(sheetType, "col1", "20 MM")} keyName="col1" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="c2" label={getHeaderLabel(sheetType, "col2", "25 MM")} keyName="col2" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="c3" label={getHeaderLabel(sheetType, "col3", "32 MM")} keyName="col3" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("elvConduitHeader")) {
 cells.push(<EditableHeader key="c4" label={getHeaderLabel(sheetType, "col4", "20 MM")} keyName="col4" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="c5" label={getHeaderLabel(sheetType, "col5", "25 MM")} keyName="col5" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="c6" label={getHeaderLabel(sheetType, "col6", "32 MM")} keyName="col6" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("perforated1Header")) {
 cells.push(<EditableHeader key="t300" label={getHeaderLabel(sheetType, "tray300mm", "300 MM")} keyName="tray300mm" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="t450" label={getHeaderLabel(sheetType, "tray450mm", "450 MM")} keyName="tray450mm" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="t600" label={getHeaderLabel(sheetType, "tray600mm", "600 MM")} keyName="tray600mm" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("ladderHeader")) {
 cells.push(<EditableHeader key="l900" label={getHeaderLabel(sheetType, "ladder900mm", "900 MM")} keyName="ladder900mm" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="l1200" label={getHeaderLabel(sheetType, "ladder1200mm", "1200 MM")} keyName="ladder1200mm" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("stripHeader")) {
 cells.push(<EditableHeader key="s253" label={getHeaderLabel(sheetType, "strip25x3", "(25x3)MM GI")} keyName="strip25x3" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="s326" label={getHeaderLabel(sheetType, "strip32x6", "(32x6)MM GI")} keyName="strip32x6" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 if (isColActive("perforated2Header")) {
 cells.push(<EditableHeader key="t150" label={getHeaderLabel(sheetType, "tray150mm", "150 MM")} keyName="tray150mm" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 cells.push(<EditableHeader key="t100" label={getHeaderLabel(sheetType, "tray100mm", "100 MM")} keyName="tray100mm" updateCustomHeader={updateCustomHeader} isApproved={formData.status === "Approved"} />);
 }
 }

 for (let i = 1; i <= 20; i++) {
 const colKey = `col${i}`;
 if (!isColActive(colKey)) continue;

 // Check if this column is part of a group (either as parent or child)
 let parentKey = null;
 for (let j = 1; j <= i; j++) {
 const pk = `col${j}`;
 const pSettings = formData.columnSettings?.[pk];
 if (isColActive(pk) && pSettings && (pSettings.colSpan > 1 || pSettings.isGroupParent)) {
 const pSpan = pSettings.colSpan || 1;
 if (i >= j && i < j + pSpan) {
 parentKey = pk;
 break;
 }
 }
 }

 if (parentKey) {
 cells.push(
 <EditableHeader 
 key={`sub-${colKey}`} 
 label={getHeaderLabel(sheetType, colKey, `Sub ${i}`)} 
 keyName={colKey} 
 onRemove={() => removeColumn(colKey)} 
 updateCustomHeader={updateCustomHeader} 
 isApproved={formData.status === "Approved"} 
 />
 );
 }
 }
 return cells;
 };

 const renderInput = (idx, field, type = "text") => {
 if (formData.status === "Approved") return <div style={{ fontSize: "12px", padding: "8px" }}>{rows[idx][field] ?? (type === "number" ? 0 : "")}</div>;
 return <input type={type} min={type === "number" ? "0" : undefined} step={type === "number" ? "0.01" : undefined} className="form-control form-control-sm" style={inputStyle} value={rows[idx][field] ?? (type === "number" ? 0 : "")} onChange={(e) => handleRowChange(idx, field, e.target.value)} />;
 };

 const renderBodyRow = (r, idx) => {
 const { sheetType } = formData;
 const cells = [];
 if (formData.boqItem && formData.boqItem.length > 1) {
 cells.push(
 <td key="boqSelect">
 {formData.status === "Approved" ? (
 <div style={{ fontSize: "12px", padding: "8px" }}>
 {(() => {
 const b = boqList.find(x => x._id === r.boqItemId);
 return b ? `${b.wlino} - ${b.itemName}` : (r.boqItemId || "-");
 })()}
 </div>
 ) : (
 <Form.Select 
 style={inputStyle} 
 value={r.boqItemId || ""} 
 onChange={e => handleRowChange(idx, "boqItemId", e.target.value)}
 >
 <option value="">Select BOQ</option>
 {formData.boqItem.map(id => {
 const b = boqList.find(x => x._id === id);
 const remainingQty = getBoqRemainingQty(id);
 const isFull = remainingQty <= 0 && r.boqItemId !== id;
 return b ? <option key={id} value={id} disabled={isFull}>{b.wlino} - {b.itemName}{remainingQty <= 0 ? " (No balance)" : ` (${remainingQty.toFixed(2)} left)`}</option> : null;
 })}
 </Form.Select>
 )}
 </td>
 );
 }
 if (isColActive("srNo")) cells.push(<td key="sr">{r.srNo}</td>);
 if (isColActive("location")) cells.push(<td key="loc">{renderInput(idx, "location")}</td>);
 if (isColActive("areaNo")) cells.push(<td key="areaN">{renderInput(idx, "areaNo")}</td>);
 if (isColActive("uom")) cells.push(<td key="uom">{renderInput(idx, "uom")}</td>);
 if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("from")) cells.push(<td key="from">{renderInput(idx, "pointFrom")}</td>);
 if (isColActive("to")) cells.push(<td key="to">{renderInput(idx, "pointTo")}</td>);
 }
 if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("from")) cells.push(<td key="from">{renderInput(idx, "cableFrom")}</td>);
 if (isColActive("to")) cells.push(<td key="to">{renderInput(idx, "cableTo")}</td>);
 }
 if (["ELECTRICAL_LIGHTING", "CONDUIT_MEASUREMENT", "CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType)) {
 if (!["CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType) && isColActive("wireSize")) cells.push(<td key="wire">{renderInput(idx, "wireSize")}</td>);
 if (isColActive("circuitNo")) cells.push(<td key="circ">{renderInput(idx, "circuitNo")}</td>);
 }
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("conduitSize")) cells.push(<td key="conduit">{renderInput(idx, "conduitSize")}</td>);
 if (isColActive("pointHeader")) { cells.push(<td key="pf">{renderInput(idx, "pointFrom")}</td>); cells.push(<td key="pt">{renderInput(idx, "pointTo")}</td>); cells.push(<td key="pq">{renderInput(idx, "pointQty", "number")}</td>); }
 if (isColActive("powerHeader")) { cells.push(<td key="pwf">{renderInput(idx, "powerFrom")}</td>); cells.push(<td key="pwt">{renderInput(idx, "powerTo")}</td>); cells.push(<td key="pwq">{renderInput(idx, "powerQty", "number")}</td>); }
 if (isColActive("subMainHeader")) { cells.push(<td key="smf">{renderInput(idx, "subMainFrom")}</td>); cells.push(<td key="smt">{renderInput(idx, "subMainTo")}</td>); cells.push(<td key="smq">{renderInput(idx, "subMainQty", "number")}</td>); }
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) { cells.push(<td key="dbsb">{renderInput(idx, "dbToSb", "number")}</td>); cells.push(<td key="db1l">{renderInput(idx, "dbTo1stLight", "number")}</td>); cells.push(<td key="sb1p">{renderInput(idx, "sbTo1stPoint", "number")}</td>); }
 if (isColActive("secondaryHeader")) { cells.push(<td key="sbsb">{renderInput(idx, "sbToSb", "number")}</td>); cells.push(<td key="ptp">{renderInput(idx, "pointToPoint", "number")}</td>); }
 if (isColActive("primaryPowerHeader")) cells.push(<td key="priP">{renderInput(idx, "primaryPowerPoints", "number")}</td>);
 if (isColActive("secondaryPowerHeader")) cells.push(<td key="secP">{renderInput(idx, "secondaryPowerPoints", "number")}</td>);
 if (sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("fanFixingHeader")) cells.push(<td key="fan">{renderInput(idx, "fanFixing", "number")}</td>);
 if (isColActive("lightFixingHeader")) cells.push(<td key="light">{renderInput(idx, "lightFixing", "number")}</td>);
 }
 } else if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) { 
 cells.push(<td key="c1">{renderInput(idx, "col1", formData.columnSettings?.col1?.fieldType || "number")}</td>); 
 cells.push(<td key="c2">{renderInput(idx, "col2", formData.columnSettings?.col2?.fieldType || "number")}</td>); 
 cells.push(<td key="c3">{renderInput(idx, "col3", formData.columnSettings?.col3?.fieldType || "number")}</td>); 
 }
 if (isColActive("elvConduitHeader")) { 
 cells.push(<td key="c4">{renderInput(idx, "col4", formData.columnSettings?.col4?.fieldType || "number")}</td>); 
 cells.push(<td key="c5">{renderInput(idx, "col5", formData.columnSettings?.col5?.fieldType || "number")}</td>); 
 cells.push(<td key="c6">{renderInput(idx, "col6", formData.columnSettings?.col6?.fieldType || "number")}</td>); 
 }
 } else if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("cableRunHeader")) cells.push(<td key="cRun">{renderInput(idx, "cableRun", "number")}</td>);
 if (isColActive("cableQtyHeader")) cells.push(<td key="cQty">{renderInput(idx, "cableQty", "number")}</td>);
 if (isColActive("totalCablesHeader")) cells.push(<td key="cTotal">{r.totalCables || 0}</td>);
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("lengthHeader")) cells.push(<td key="len">{renderInput(idx, "length", "number")}</td>);
 if (isColActive("runHeader")) cells.push(<td key="run">{renderInput(idx, "run", "number")}</td>);
 if (isColActive("perforated1Header")) { cells.push(<td key="t300">{renderInput(idx, "tray300mm", "number")}</td>); cells.push(<td key="t450">{renderInput(idx, "tray450mm", "number")}</td>); cells.push(<td key="t600">{renderInput(idx, "tray600mm", "number")}</td>); }
 if (isColActive("ladderHeader")) { cells.push(<td key="l900">{renderInput(idx, "ladder900mm", "number")}</td>); cells.push(<td key="l1200">{renderInput(idx, "ladder1200mm", "number")}</td>); }
 if (isColActive("stripHeader")) { cells.push(<td key="s253">{renderInput(idx, "strip25x3", "number")}</td>); cells.push(<td key="s326">{renderInput(idx, "strip32x6", "number")}</td>); }
 if (isColActive("perforated2Header")) { cells.push(<td key="t150">{renderInput(idx, "tray150mm", "number")}</td>); cells.push(<td key="t100">{renderInput(idx, "tray100mm", "number")}</td>); }
 }
 
 // Catch-all for any active colX not handled by specific sheet types
 for (let i = 1; i <= 20; i++) { 
 const colKey = `col${i}`;
 if (isColActive(colKey)) {
 if (sheetType === "CONDUIT_SUMMARY" && i <= 6) continue;
 const fieldType = formData.columnSettings?.[colKey]?.fieldType || "number";
 cells.push(<td key={colKey}>{renderInput(idx, colKey, fieldType === "number" ? "number" : "text")}</td>);
 }
 }
 if (isColActive("remarks")) cells.push(<td key="rem">{renderInput(idx, "remarks")}</td>);
 if (isColActive("action") && formData.status !== "Approved") cells.push(<td key="act" className="text-center"><Button variant="danger" size="xs" onClick={() => removeRow(idx)}><i className="fa fa-trash"></i></Button></td>);
 return cells;
 };

 const renderFooterRow = () => {
 const { sheetType } = formData;
 const cells = [];
 let emptySpan = [ "srNo", "location", "areaNo", "uom", "from", "to", "wireSize", "circuitNo", "conduitSize" ].filter(c => isColActive(c)).length;
 if (formData.boqItem && formData.boqItem.length > 1) {
 emptySpan += 1;
 }
 if (emptySpan > 0) cells.push(<td key="empty" colSpan={emptySpan} className="fw-bold text-end">TOTAL</td>);
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("pointHeader")) { cells.push(<td key="pf" colSpan={2}></td>); cells.push(<td key="pq" className="fw-bold">{totals.pointQty}</td>); }
 if (isColActive("powerHeader")) { cells.push(<td key="pwf" colSpan={2}></td>); cells.push(<td key="pwq" className="fw-bold">{totals.powerQty}</td>); }
 if (isColActive("subMainHeader")) { cells.push(<td key="smf" colSpan={2}></td>); cells.push(<td key="smq" className="fw-bold">{totals.subMainQty}</td>); }
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) { cells.push(<td key="dbsb" className="fw-bold">{totals.primaryPointDbToSb}</td>); cells.push(<td key="db1l" className="fw-bold">{totals.primaryPointDbTo1stLight}</td>); cells.push(<td key="sb1p" className="fw-bold">{totals.primaryPointSbTo1stPoint}</td>); }
 if (isColActive("secondaryHeader")) { cells.push(<td key="sbsb" className="fw-bold">{totals.secondaryPointSbToSb}</td>); cells.push(<td key="ptp" className="fw-bold">{totals.secondaryPointPointToPoint}</td>); }
 if (isColActive("primaryPowerHeader")) cells.push(<td key="priP" className="fw-bold">{totals.primaryPowerPoints}</td>);
 if (isColActive("secondaryPowerHeader")) cells.push(<td key="secP" className="fw-bold">{totals.secondaryPowerPoints}</td>);
 if (sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("fanFixingHeader")) cells.push(<td key="fan" className="fw-bold">{totals.fanFixing}</td>);
 if (isColActive("lightFixingHeader")) cells.push(<td key="light" className="fw-bold">{totals.lightFixing}</td>);
 }
 } else if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) { cells.push(<td key="c1" className="fw-bold">{totals.conduit20mm}</td>); cells.push(<td key="c2" className="fw-bold">{totals.conduit25mm}</td>); cells.push(<td key="c3" className="fw-bold">{totals.conduit32mm}</td>); }
 if (isColActive("elvConduitHeader")) { cells.push(<td key="c4" className="fw-bold">{totals.elvConduit20mm}</td>); cells.push(<td key="c5" className="fw-bold">{totals.elvConduit25mm}</td>); cells.push(<td key="c6" className="fw-bold">{totals.elvConduit32mm}</td>); }
 } else if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("cableRunHeader")) cells.push(<td key="cRun" className="fw-bold">{totals.cableRun}</td>);
 if (isColActive("cableQtyHeader")) cells.push(<td key="cQty" className="fw-bold">{totals.cableQty}</td>);
 if (isColActive("totalCablesHeader")) cells.push(<td key="cTotal" className="fw-bold">{totals.totalCables}</td>);
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("lengthHeader")) cells.push(<td key="len" className="fw-bold">{totals.length}</td>);
 if (isColActive("runHeader")) cells.push(<td key="run" className="fw-bold">{totals.run}</td>);
 if (isColActive("perforated1Header")) { cells.push(<td key="t300" className="fw-bold">{totals.tray300mm}</td>); cells.push(<td key="t450" className="fw-bold">{totals.tray450mm}</td>); cells.push(<td key="t600" className="fw-bold">{totals.tray600mm}</td>); }
 if (isColActive("ladderHeader")) { cells.push(<td key="l900" className="fw-bold">{totals.ladder900mm}</td>); cells.push(<td key="l1200" className="fw-bold">{totals.ladder1200mm}</td>); }
 if (isColActive("stripHeader")) { cells.push(<td key="s253" className="fw-bold">{totals.strip25x3}</td>); cells.push(<td key="s326" className="fw-bold">{totals.strip32x6}</td>); }
 if (isColActive("perforated2Header")) { cells.push(<td key="t150" className="fw-bold">{totals.tray150mm}</td>); cells.push(<td key="t100" className="fw-bold">{totals.tray100mm}</td>); }
 }
 
 // Catch-all for any active colX not handled by specific sheet types
 for (let i = 1; i <= 20; i++) { 
 const colKey = `col${i}`;
 if (isColActive(colKey)) {
 // If it was already handled by sheet-specific logic, we don't want to duplicate it.
 // But the current structure doesn't easily track that, and most types don't use col1-20
 // except CONDUIT_SUMMARY (col1-6).
 if (sheetType === "CONDUIT_SUMMARY" && i <= 6) continue;
 
 const fieldType = formData.columnSettings?.[colKey]?.fieldType || "number";
 cells.push(<td key={colKey} className="fw-bold">{fieldType === "number" ? totals[colKey] : ""}</td>); 
 }
 }
 if (isColActive("remarks")) cells.push(<td key="rem"></td>);
 if (isColActive("action") && formData.status !== "Approved") cells.push(<td key="act"></td>);
 return cells;
 };

 const secondHeaderCells = renderRow2();

 return (
 <>
 <PageTitle activeMenu="Edit Measurement Sheet" motherMenu="Site" />
 <Card>
 <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
 <div className="d-flex align-items-center gap-3">
 <h4 className="mb-0 fw-bold">Edit Measurement Sheet</h4>
 {isAutoSaving && (
 <Badge bg="light" className="text-muted border d-flex align-items-center gap-2" style={{ fontSize: "11px" }}>
 <div className="spinner-border spinner-border-sm" style={{ width: "10px", height: "10px" }}></div>
 Auto-saving draft...
 </Badge>
 )}
 {!isAutoSaving && formData.status !== "Approved" && (
 <Badge bg="success-light" className="text-success border" style={{ fontSize: "11px" }}>
 <i className="fa fa-check-circle me-1"></i> Draft Saved
 </Badge>
 )}
 </div>
 </Card.Header>
 <Card.Body><Form onSubmit={handleSubmit}>
 <Row className="mb-4">
 <Col md={4}><Form.Group className="mb-3"><Form.Label>Work Order</Form.Label><Select options={workOrders.map(w => ({ value: w._id, label: `${w.woNo}${w.label ? ` – ${w.label}` : ""} - ${w.project?.projectName || "Unknown"}` }))} value={workOrders.find(w => w._id === formData.workOrder) ? { value: formData.workOrder, label: `${workOrders.find(w => w._id === formData.workOrder).woNo}${workOrders.find(w => w._id === formData.workOrder).label ? ` – ${workOrders.find(w => w._id === formData.workOrder).label}` : ""} - ${workOrders.find(w => w._id === formData.workOrder).project?.projectName || "Unknown"}` } : null} onChange={handleWorkOrderChange} isDisabled={formData.status === "Approved"} styles={selectStyles} menuPortalTarget={document.body} /></Form.Group></Col>
 <Col md={4}><Form.Group className="mb-3"><Form.Label>BOQ Item</Form.Label><Select isMulti options={boqList.map(b => ({ value: b._id, label: `${b.wlino || ""} - ${b.itemName}` }))} value={(() => {
 const items = Array.isArray(formData.boqItem) ? formData.boqItem : (formData.boqItem ? [formData.boqItem] : []);
 return items.map(id => {
 const b = boqList.find(x => x._id === id);
 return b ? { value: b._id, label: `${b.wlino || ""} - ${b.itemName}` } : null;
 }).filter(Boolean);
 })()} onChange={handleBOQChange} isDisabled={formData.status === "Approved"} styles={selectStyles} menuPortalTarget={document.body} /></Form.Group></Col>
 <Col md={4}><Form.Group className="mb-3"><Form.Label>Sheet Type</Form.Label><Form.Select value={formData.sheetType} onChange={(e) => handleSheetTypeChange(e.target.value)} disabled={formData.status === "Approved"}>{sheetTypeMasters.map((type) => <option key={type._id || type.code} value={type.code}>{type.name || type.code}</option>)}</Form.Select></Form.Group></Col>
 </Row>
 <Row className="mb-4">
 <Col md={3}><Form.Group><Form.Label>Item Name</Form.Label><Form.Control value={formData.itemName} readOnly /></Form.Group></Col>
 <Col md={3}><Form.Group><Form.Label>Unit</Form.Label><Form.Control value={formData.unit} readOnly /></Form.Group></Col>
 <Col md={3}><Form.Group><Form.Label>Approved Qty</Form.Label><Form.Control value={formData.approvedQty} readOnly /></Form.Group></Col>
 <Col md={3}><Form.Group><Form.Label>Used Qty (Before)</Form.Label><Form.Control value={formData.usedQtyBefore} readOnly /></Form.Group></Col>
 </Row>
 <Row className="mb-4">
 <Col md={3}><Form.Group><Form.Label>Remaining Qty</Form.Label><Form.Control value={formData.remainingQtyBefore} readOnly /></Form.Group></Col>
 <Col md={3}><Form.Group><Form.Label>Calculated Qty</Form.Label><Form.Control value={formData.calculatedQty} readOnly /></Form.Group></Col>
 <Col md={3}><Form.Group><Form.Label>Installed Qty (Final)</Form.Label><Form.Control type="number" min="0" max={getRemainingQty()} step="0.01" value={formData.installedQty} onChange={(e) => setFormData(prev => ({ ...prev, installedQty: Math.min(toNonNegativeNumber(e.target.value), getRemainingQty(prev)) }))} readOnly={formData.status === "Approved"} /></Form.Group></Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 value={formData.status}
 onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
 disabled={saving}
 >
 <option value="Draft">Draft</option>
 <option value="Submitted">Submitted</option>
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>
 <div className="table-responsive mb-4" style={{ overflow: "visible" }}>
 <Table bordered size="sm" className="mb-0 custom-measurement-table">
 <thead><tr style={{ backgroundColor: "#92D050" }}>{renderRow1()}</tr>{(secondHeaderCells.length > 0 && formData.sheetType !== "CABLE_MEASUREMENT" && formData.sheetType !== "SWITCH_SOCKET" && formData.sheetType !== "DB_INSTALLATION") && <tr style={{ backgroundColor: "#92D050" }}>{secondHeaderCells}</tr>}</thead>
 <tbody>{rows.map((r, idx) => <tr key={idx}>{renderBodyRow(r, idx)}</tr>)}<tr className="bg-light">{renderFooterRow()}</tr></tbody>
 </Table>
 </div>
 {formData.status !== "Approved" && <div className="d-flex justify-content-between mb-4"><Button variant="info" size="sm" onClick={addRow}>+ Add Row</Button><Button variant="secondary" size="sm" onClick={openGroupModal}>+ Group Column</Button></div>}
 <div className="text-end"><Button variant="light" className="me-2" onClick={() => navigate(-1)}>Cancel</Button><Button variant="primary" type="submit" disabled={saving}>{saving ? "Updating..." : "Update Measurement Sheet"}</Button></div>
 </Form></Card.Body></Card>
 <Modal show={showModal} onHide={() => setShowModal(false)} centered>
 <Modal.Header closeButton><Modal.Title className="fw-bold">Add New Column(s)</Modal.Title></Modal.Header>
 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label className="fw-bold small">Heading Name *</Form.Label>
 <Form.Control 
 placeholder="e.g. LIGHT POINTS" 
 value={modalData.parent} 
 onChange={e => setModalData({ ...modalData, parent: e.target.value })} 
 />
 <Form.Text className="text-muted">This will be the main heading for your column(s).</Form.Text>
 </Form.Group>
 
 <Form.Group className="mb-3">
 <Form.Label className="fw-bold small">Input Type *</Form.Label>
 <div className="d-flex gap-4">
 <Form.Check 
 type="radio" 
 label="Number (e.g. 10, 20.5)" 
 name="fieldType" 
 checked={modalData.fieldType === "number"} 
 onChange={() => setModalData({ ...modalData, fieldType: "number" })} 
 />
 <Form.Check 
 type="radio" 
 label="Text (e.g. Remarks, Names)" 
 name="fieldType" 
 checked={modalData.fieldType === "text"} 
 onChange={() => setModalData({ ...modalData, fieldType: "text" })} 
 />
 </div>
 </Form.Group>

 <hr />

 <Form.Group className="mb-2">
 <Form.Label className="fw-bold small d-flex justify-content-between align-items-center">
 Sub-Headings (Optional)
 <Badge bg="info-light" className="text-info fw-normal">Leave empty for single column</Badge>
 </Form.Label>
 {modalData.subs.map((s, idx) => (
 <div key={idx} className="d-flex gap-2 mb-2">
 <Form.Control 
 size="sm" 
 placeholder={`Sub-heading ${idx + 1}`} 
 value={s} 
 onChange={e => { 
 const newSubs = [...modalData.subs]; 
 newSubs[idx] = e.target.value; 
 setModalData({ ...modalData, subs: newSubs }); 
 }} 
 />
 <Button 
 variant="outline-danger" 
 size="sm" 
 onClick={() => setModalData({ ...modalData, subs: modalData.subs.filter((_, i) => i !== idx) })} 
 disabled={modalData.subs.length === 1 && !modalData.subs[0]}
 >
 <i className="fa fa-times"></i>
 </Button>
 </div>
 ))}
 <Button 
 variant="outline-primary" 
 size="sm" 
 className="mt-1" 
 onClick={() => setModalData({ ...modalData, subs: [...modalData.subs, ""] })}
 >
 <i className="fa fa-plus me-1"></i>Add Another Sub-heading
 </Button>
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={handleModalSave} className="px-4">Create Column(s)</Button>
 </Modal.Footer>
 </Modal>
 </>
 );
};

export default MeasurementSheetEdit;
